import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'
import {
  usePrintSaleAmendmentEnMutation,
  usePrintSaleAmendmentVnMutation,
  type SaleAmendment,
} from '../../store/api/saleAmendmentsApi'
import { usePagePermission } from '../../hooks/usePagePermission'

interface SaleAmendmentPrintDialogProps {
  open: boolean
  language: 'en' | 'vn'
  amendment: SaleAmendment | null
  onClose: () => void
}

const PRINT_EN_PREVIEW_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
.grid-logo { display: flex !important; align-items: center !important; height: 17mm !important; margin-bottom: 1mm !important; overflow: hidden !important; }
.grid-logo img { width: 50mm !important; max-width: 50mm !important; max-height: 15mm !important; height: auto !important; object-fit: contain !important; object-position: left center !important; }
.print-action { display: none !important; }
.amend-title { font-size: 14pt !important; font-weight: 700 !important; text-align: center !important; margin: 2mm 0 1mm !important; line-height: 1.3 !important; }
.amend-sub { text-align: center !important; font-weight: 700 !important; margin: 0.6mm 0 !important; }
.amend-date { text-align: right !important; margin: 1mm 0 !important; }
.party-row { display: grid !important; grid-template-columns: 30mm minmax(0, 1fr) !important; column-gap: 2mm !important; margin: 0.8mm 0 !important; }
.party-detail { padding-left: 32mm !important; margin: 0.5mm 0 !important; }
.amend-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; margin: 2mm 0 !important; }
.amend-head { text-align: center !important; font-weight: 700 !important; text-decoration: underline !important; border: 1px solid #111827 !important; border-bottom: none !important; padding: 1mm 2mm !important; }
.amend-head + .amend-head { border-left: none !important; }
.amend-body { border: 1px solid #111827 !important; border-top: none !important; padding: 2mm !important; min-height: 22mm !important; white-space: pre-wrap !important; overflow-wrap: anywhere !important; }
.amend-body + .amend-body { border-left: none !important; }
.signatures { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 10mm !important; margin-top: 8mm !important; }
@media print { .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 12mm 14mm !important; } }
`.trim()

const PRINT_VN_PREVIEW_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
.grid-logo { display: flex !important; align-items: center !important; height: 17mm !important; margin-bottom: 1mm !important; overflow: hidden !important; }
.grid-logo img { width: 50mm !important; max-width: 50mm !important; max-height: 15mm !important; height: auto !important; object-fit: contain !important; object-position: left center !important; }
.print-action { display: none !important; }
.amend-title { font-size: 14pt !important; font-weight: 700 !important; text-align: center !important; margin: 2mm 0 1mm !important; line-height: 1.3 !important; }
.amend-sub { text-align: center !important; font-weight: 700 !important; margin: 0.6mm 0 !important; }
.party-row { display: grid !important; grid-template-columns: 30mm minmax(0, 1fr) !important; column-gap: 2mm !important; margin: 0.8mm 0 !important; }
.party-detail { padding-left: 32mm !important; margin: 0.5mm 0 !important; }
.party-detail-2 { display: grid !important; grid-template-columns: 26mm minmax(0, 1fr) !important; column-gap: 2mm !important; padding-left: 32mm !important; margin: 0.5mm 0 !important; }
.party-detail-row { display: grid !important; grid-template-columns: 26mm minmax(0, 1fr) 20mm minmax(0, 1fr) !important; column-gap: 2mm !important; padding-left: 32mm !important; margin: 0.5mm 0 !important; }
.amend-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; margin: 2mm 0 !important; }
.amend-head { text-align: center !important; font-weight: 700 !important; text-decoration: underline !important; border: 1px solid #111827 !important; border-bottom: none !important; padding: 1mm 2mm !important; }
.amend-head + .amend-head { border-left: none !important; }
.amend-body { border: 1px solid #111827 !important; border-top: none !important; padding: 2mm !important; min-height: 22mm !important; white-space: pre-wrap !important; overflow-wrap: anywhere !important; }
.amend-body + .amend-body { border-left: none !important; }
.signatures { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 10mm !important; margin-top: 8mm !important; }
.sig-sign-name { font-weight: 700 !important; margin-top: 20mm !important; text-transform: uppercase !important; }
@media print { .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 12mm 14mm !important; } }
`.trim()

function buildPreviewHtml(rawHtml: string, language: 'en' | 'vn'): string {
  const css = language === 'vn' ? PRINT_VN_PREVIEW_CSS : PRINT_EN_PREVIEW_CSS
  const htmlWithoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = htmlWithoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="sale-amendment-preview-overrides">${css}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function SaleAmendmentPrintDialog({ open, language, amendment, onClose }: SaleAmendmentPrintDialogProps) {
  const { canEdit } = usePagePermission()
  const [previewing, setPreviewing] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [printEn] = usePrintSaleAmendmentEnMutation()
  const [printVn] = usePrintSaleAmendmentVnMutation()

  const previewTitle = language === 'vn' ? 'Print Preview - Sale Amendment VN' : 'Print Preview - Sale Amendment EN'
  const iframeTitle = language === 'vn' ? 'Sale Amendment VN Preview' : 'Sale Amendment EN Preview'

  useEffect(() => {
    if (!open || !amendment || previewing || previewHtml) return
    let cancelled = false
    const run = async () => {
      setPreviewing(true)
      try {
        const result =
          language === 'vn'
            ? await printVn(amendment.amendmentNo).unwrap()
            : await printEn(amendment.amendmentNo).unwrap()
        if (cancelled) return
        setPreviewHtml(buildPreviewHtml(result.html, language))
        setToastMessage(result.message ?? 'Print preview generated.')
      } catch {
        if (!cancelled) {
          setToastMessage('Failed to generate print preview.')
          onClose()
        }
      } finally {
        if (!cancelled) setPreviewing(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, amendment])

  const handleClosePreview = () => {
    setPreviewHtml('')
    onClose()
  }

  return (
    <>
      {previewing && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog open={Boolean(previewHtml)} onClose={handleClosePreview} maxWidth={false} fullWidth PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={previewTitle}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {language === 'vn' ? 'Sale Amendment VN' : 'Sale Amendment EN'}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              {language === 'vn' ? 'Amend VN' : 'Amend EN'}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClosePreview} size="small">
              Close
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  const printTitle = `${sanitizePdfFileNamePart(amendment?.amendmentNo ?? 'amendment')}_Amendment_${language.toUpperCase()}.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: language === 'vn' ? 'rpContractSalesAmendment_VN' : 'rpContractSalesAmendment_EN',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2000}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
